import React, { useCallback, useEffect, useRef, useState } from "react";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { MAIN_URL } from "../../../Configurations/Urls";

const DEFAULT_COLUMNS = [
  {
    field: "recruitment_source_type_name",
    label: "Source Type Name",
    visible: true,
    width: 220,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "description",
    label: "Description",
    visible: true,
    width: 280,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_active",
    label: "Is Active",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_recruitment_source_type_id",
  sortOrder: "asc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    ) {
      return;
    }

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => {
            const trimmed = v.trim();
            return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
          });
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

const normalizeAppliedFilters = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter((f) => f?.field && f?.operator)
    .map((f) => {
      const requiresValue = operatorRequiresValue(f.operator);
      if (!requiresValue) {
        return { field: f.field, operator: f.operator, value: null };
      }

      let value = f.value;
      if (value === null || value === undefined || value === "") return null;
      if (Array.isArray(value) && value.length === 0) return null;

      if (["equals", "not_equals"].includes(f.operator) && typeof value === "string") {
        value = value.split(",").map((v) => {
          const trimmed = v.trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      } else if (
        ["equals", "not_equals"].includes(f.operator) &&
        Array.isArray(value)
      ) {
        value = value.map((v) => {
          const trimmed = String(v).trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      }

      return { field: f.field, operator: f.operator, value };
    })
    .filter(Boolean);
};

function RecruitmentSourceTypesList() {
  const [rows, setRows] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  // null = derive from URL; array = last applied filters from the filter modal
  const [appliedFilters, setAppliedFilters] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) setSearchParams(params, { replace: true });
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }
      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (configRes.ok) {
          const configResponse = await configRes.json();
          const orgKey = `organization_recruitment_source_types_grid_${org.organization_id}`;
          const savedConfig = (configResponse.datagrids || []).find(
            (dg) => dg.datagrid_key === orgKey
          );
          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            setConfigColumns(
              serverCfg?.columns?.length ? serverCfg.columns : DEFAULT_COLUMNS
            );
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error(error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadTableConfiguration();
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const search = searchParams.get("search");
        const filters =
          appliedFilters !== null
            ? normalizeAppliedFilters(appliedFilters)
            : buildFiltersFromParams(searchParams);

        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field:
              searchParams.get("sortBy") ||
              "organization_recruitment_source_type_id",
            direction: searchParams.get("sortOrder") || "asc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-types-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page: result.pagination.page || 1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setRows(
          data.map((item) => ({
            ...item,
            id: item?.organization_recruitment_source_type_id,
            is_active:
              item?.is_active == 1 || item?.is_active === true ? "✔" : "✖",
          }))
        );
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error(error);
        toast.error("Failed to load recruitment source types");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => fetchControllerRef.current?.abort();
  }, [org?.organization_id, searchParams, appliedFilters]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-type/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Source type deleted");
          setAppliedFilters((prev) => (prev === null ? [] : [...prev]));
        } else {
          const errorData = await response.json();
          toast.error(errorData.message || "Failed to delete source type");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete source type");
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/recruitment/source-types/edit/${item.id || item.organization_recruitment_source_type_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/recruitment/source-types/view/${item.id || item.organization_recruitment_source_type_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  // Use filters from the table directly — do NOT clone URLSearchParams here.
  // React Router passes the stale searchParams into updater fns, so cloning after
  // updateURL would wipe filter_* params and the list never filtered.
  const handleFilterChange = useCallback((nextFilters) => {
    setAppliedFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Recruitment Source Types"}
        btnName={"Add Source Type"}
        add_action={"SOURCE_TYPES_ADD"}
        delete_action={"SOURCE_TYPES_DELETE"}
        Data={rows}
        Icons={[
          <CategoryIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <PersonIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "source types",
          "recruitment source types",
          "Add Source Type",
          "source type",
        ]}
        Route={"/recruitment/source-types"}
        setData={setRows}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_recruitment_source_types"
          primaryKey="organization_recruitment_source_type_id"
          heading="Recruitment Source Types"
          data={rows}
          sortname="organization_recruitment_source_type_id"
          showActions={true}
          Route="/recruitment/source-types"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          edit_delete_action={[
            "SOURCE_TYPES_DELETE",
            "SOURCE_TYPES_EDIT",
          ]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_recruitment_source_types"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default RecruitmentSourceTypesList;
